import { notification } from 'antd';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { useLazyGetBranchesForSystemQuery } from '@app/api/Branches';
import { useLazyGetPositionsForSystemQuery } from '@app/api/Positions';
import {
  useLazyGetUsersSearchResultQuery,
  useSearchUsersMutation,
} from '@app/api/Users';
import { EDIT_USER, MOBILE } from '@app/constants';
import { navigation } from '@app/data';
import {
  useUsersAccessRights,
  useMemoizeObject,
  useUserPrint,
  useWindowSize,
} from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import { useGetAiQuery } from '@app/services/rtkQuery/organization';
import type { NAnalytics, NSmallGlobalEntities } from '@app/types';
import type { NBranches } from '@app/types/Branches';
import { addEnterKeyListener, changeStandardPaginationUi } from '@app/utils';

import type { InputRef } from 'antd';
import type { BaseOptionType } from 'antd/es/select';
import type { ChangeEventHandler } from 'react';

const DEFAULT_ROWS_COUNT = 20;
const DEFAULT_PAGE_NUMBER = 1;

export const useUsers = () => {
  const { userId, database, activeBranchId } = useUserPrint();
  const [positions, setPositions] = useState<NSmallGlobalEntities.ModelItem[]>(
    []
  );
  const [data, setData] = useState<NAnalytics.ModelUserLite[]>([]);
  const [totalRowsCount, setTotalRowsCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchId, setSearchId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoader, setLoader] = useState(false);
  const [allBranches, setAllBranches] = useState<NBranches.BranchesForSystem[]>(
    []
  );
  const [selectedBranches, setSelectedBranches] = useState<
    BaseOptionType[] | null
  >(null);
  const [selectedPositions, setSelectedPositions] = useState<
    BaseOptionType[] | null
  >(null);
  const [selectedStatuses, setSelectedStatuses] = useState<
    BaseOptionType[] | null
  >(null);
  const ref = useRef<InputRef | null>(null);
  const [searchQueryError, setSearchQueryError] = useState('');
  const [, contextHolder] = notification.useNotification();
  const { showNotification } = useContext(NotificationContext);

  const { innerWidth } = useWindowSize();
  const isMobile = innerWidth <= MOBILE;

  const memoizedSelectedBranches = useMemoizeObject(selectedBranches);
  const memoizedSelectedPositions = useMemoizeObject(selectedPositions);
  const memoizedSelectedStatuses = useMemoizeObject(selectedStatuses);

  const { data: organizationsWithAccessToAi } = useGetAiQuery(
    { ActiveBranchId: activeBranchId },
    { skip: activeBranchId === null }
  );
  const [getBranchesForSystem] = useLazyGetBranchesForSystemQuery();
  const [getPositionsForSystem] = useLazyGetPositionsForSystemQuery();
  const [searchUsers] = useSearchUsersMutation();
  const [getUsersSearchResult] = useLazyGetUsersSearchResultQuery();

  const getRequestData = useCallback(
    (isDefaultSearch?: boolean) => {
      const _searchQuery = ref?.current?.input?.value
        ? ref.current.input.value
        : searchQuery;

      const data = isDefaultSearch
        ? {
            SearchTerm: '',
            BranchIds: [activeBranchId],
            PositionIds: null,
            Statuses: null,
            UsersPerPage: DEFAULT_ROWS_COUNT,
          }
        : {
            SearchTerm: _searchQuery,
            BranchIds:
              memoizedSelectedBranches && memoizedSelectedBranches.length > 0
                ? memoizedSelectedBranches?.map(({ value }) => value)
                : null,
            PositionIds: memoizedSelectedPositions?.length
              ? memoizedSelectedPositions.map(({ value }) => value)
              : null,
            Statuses:
              memoizedSelectedStatuses?.map(({ value }) => value) || null,
            UsersPerPage: rowsPerPage,
          };

      return data;
    },
    [
      activeBranchId,
      memoizedSelectedBranches,
      memoizedSelectedPositions,
      memoizedSelectedStatuses,
      rowsPerPage,
      searchQuery,
    ]
  );

  const showData = useCallback(
    async (isDefaultSearch?: boolean) => {
      if (!searchQueryError) {
        const _searchQuery = ref?.current?.input?.value
          ? ref.current.input.value
          : searchQuery;
        if (_searchQuery.length >= 3 || _searchQuery.length === 0) {
          setSearchQuery(_searchQuery);
          try {
            setLoader(true);

            const { Users, Id, CountUsers } = await searchUsers({
              ActiveBranchId: activeBranchId,
              data: getRequestData(isDefaultSearch),
            }).unwrap();

            setTotalRowsCount(CountUsers);
            setData(Users || []);
            setPageNumber(DEFAULT_PAGE_NUMBER);
            setSearchId(String(Id));
          } catch (error) {
            showNotification(handleCatchError(error));
          } finally {
            setLoader(false);
          }
        }
      }
    },
    [
      searchQueryError,
      searchQuery,
      activeBranchId,
      getRequestData,
      showNotification,
      searchUsers,
    ]
  );

  useEffect(() => {
    return addEnterKeyListener({
      callback: showData,
    });
  }, [showData]);

  const getDefaultBranch = useCallback(
    (allBranches: NBranches.BranchesForSystem[]) => {
      const defaultBranch = allBranches.find(
        (branch: NBranches.BranchesForSystem) => branch.Id === activeBranchId
      );
      const adaptedDefaultBranch = {
        label: defaultBranch?.Name,
        value: defaultBranch?.Id,
      };
      return adaptedDefaultBranch;
    },
    [activeBranchId]
  );

  useEffect(() => {
    const getInitialData = async () => {
      const arePreConditionsDone =
        activeBranchId && organizationsWithAccessToAi !== undefined;

      if (!arePreConditionsDone) {
        return;
      }

      try {
        setLoader(true);

        const { Users, Id, CountUsers } = await searchUsers({
          ActiveBranchId: activeBranchId,
          data: {
            SearchTerm: '',
            BranchIds: [activeBranchId],
            PositionIds: null,
            Statuses: null,
            UsersPerPage: DEFAULT_ROWS_COUNT,
          },
        }).unwrap();

        setTotalRowsCount(CountUsers);
        setData(Users || []);
        setPageNumber(DEFAULT_PAGE_NUMBER);
        setRowsPerPage(DEFAULT_ROWS_COUNT);
        setSearchId(String(Id));

        const positionsResult = await getPositionsForSystem({
          ActiveBranchId: activeBranchId,
        }).unwrap();

        setPositions([
          { Id: 0, IdS: null, Name: 'Без должности' },
          ...positionsResult,
        ]);
      } catch (error) {
        showNotification(handleCatchError(error));
      } finally {
        setLoader(false);
      }
    };

    getInitialData();
  }, [
    activeBranchId,
    getDefaultBranch,
    getPositionsForSystem,
    showNotification,
    organizationsWithAccessToAi,
    searchUsers,
  ]);

  const fetchBranchesForSystem = useCallback(async () => {
    try {
      const response = await getBranchesForSystem({
        ActiveBranchId: activeBranchId,
      }).unwrap();
      setAllBranches(response);
      setSelectedBranches([getDefaultBranch(response)]);
    } catch (error) {
      showNotification(error as Error);
    }
  }, [
    activeBranchId,
    getBranchesForSystem,
    getDefaultBranch,
    showNotification,
  ]);

  useEffect(() => {
    if (userId && database && activeBranchId) {
      fetchBranchesForSystem();
    }
  }, [
    activeBranchId,
    database,
    fetchBranchesForSystem,
    getDefaultBranch,
    showNotification,
    userId,
  ]);

  useEffect(() => {
    changeStandardPaginationUi({
      isMobile,
    });
  }, [data, isMobile]);

  const resetAll = () => {
    if (ref.current?.input) {
      ref.current.input.value = '';
    }
    showData(true);
    setSearchQuery('');
    setSelectedBranches([getDefaultBranch(allBranches)]);
    setSelectedPositions(null);
    setSelectedStatuses(null);
  };

  const handlePageChange = async (page: number, pageSize: number) => {
    if (searchId) {
      try {
        setLoader(true);
        const { Users, CountUsers, Filter, PageN } = await getUsersSearchResult(
          {
            ActiveBranchId: activeBranchId,
            page: pageSize === rowsPerPage ? page : 1,
            searchId,
            rowsPerPage: pageSize,
          }
        ).unwrap();

        setData(Users || []);
        setTotalRowsCount(CountUsers);

        setRowsPerPage(Filter.UsersPerPage);
        setPageNumber(PageN);
      } catch (error) {
        showNotification(handleCatchError(error));
      } finally {
        setLoader(false);
      }
    }
  };

  const navigate = useNavigate();
  const editUserPath = useMemo(
    () => navigation.find(({ id }) => id === EDIT_USER)?.path || '',
    []
  );

  const handleEdit = useCallback(
    (id: number) => {
      navigate(`${editUserPath}?id=${id}&key=2`);
    },
    [editUserPath, navigate]
  );

  const handleHistoryIconClick = useCallback(
    (id: number) => {
      navigate(`${editUserPath}?id=${id}&key=5`);
    },
    [editUserPath, navigate]
  );

  const { hasUserRightToUserRegistry, hasUserRightToEdit } =
    useUsersAccessRights();

  const onChangeSearchInput: ChangeEventHandler<HTMLInputElement> = useCallback(
    ({ target }) => {
      const { value } = target;
      const hasMoreThan3Symbols = value && value.length < 3;

      setSearchQueryError(hasMoreThan3Symbols ? 'Минимум: 3 символа' : '');

      setSearchQuery(value);
    },
    []
  );
  return {
    searchQuery,
    searchQueryError,
    isLoader,
    ref,
    totalRowsCount,
    pageNumber,
    data,
    rowsPerPage,
    allBranches,
    selectedBranches,
    positions,
    selectedPositions,
    selectedStatuses,
    setSelectedStatuses,
    setSelectedPositions,
    setSelectedBranches,
    showData,
    resetAll,
    handlePageChange,
    isMobile,
    contextHolder,
    handleEdit,
    handleHistoryIconClick,
    hasUserRightToUserRegistry,
    hasUserRightToEdit,
    onChangeSearchInput,
    navigate,
  };
};
